import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { NotFound } from "./pages/NotFound";
import { useEffect, useMemo, useState } from "react";

import Layout from "./pages/Layout";
import GanttChartPage from "./pages/GanttChartPage";
import { refreshAccessToken } from "./service/api";
import KPIPage from "./pages/KPIPage";
import NexusLandingPage from "./pages/NexusLandingPage";
import { useAppDispatch } from "./store/hooks";
import AppInitializationService from "./service/AppInitializationService";
import CalendarSettingsPage from "./pages/CalendarSettingsPage";
import ConstraintPage from "./pages/ConstraintPage";
import {
  isConstraintShown,
  isCalendarSettingsShown,
} from "./utils/environmentUtils";

// The login platform hands us the access/refresh tokens as one-time query
// params. Capture them at module load and strip them from the URL *before*
// createBrowserRouter() snapshots the location below — so the router (and every
// useLocation/useSearchParams consumer) never carries the tokens, navigation
// can't replay them, and a reload/tab-restore won't re-apply stale ones. Doing
// it here (not via setSearchParams in a component) avoids racing the navigation
// that sets date/filter. Other params are preserved.
const initialUrlParams = new URLSearchParams(window.location.search);
const initialLoginToken = initialUrlParams.get("token");
const initialLoginRefreshToken = initialUrlParams.get("refreshToken");

if (initialLoginToken || initialLoginRefreshToken) {
  initialUrlParams.delete("token");
  initialUrlParams.delete("refreshToken");
  const remaining = initialUrlParams.toString();
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}${remaining ? `?${remaining}` : ""}${window.location.hash}`,
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      { path: "/", element: <GanttChartPage /> },
      { path: "/overview", element: <KPIPage /> },
      { path: "/ganttChart", element: <GanttChartPage /> },
      {
        path: "/constraint",
        element: isConstraintShown ? <ConstraintPage /> : <NotFound />,
      },
      {
        path: "/calendarSettings",
        element: isCalendarSettingsShown ? (
          <CalendarSettingsPage />
        ) : (
          <NotFound />
        ),
      },
      { path: "/nexus-landing", element: <NexusLandingPage /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

const App = () => {
  const [parametersHandled, setParametersHandled] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const isNexusLanding = (): boolean =>
    window.location.pathname.toLowerCase() === "/nexus-landing";

  // Perform on load actions
  const appInitializationService = useMemo(() => {
    return new AppInitializationService(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (parametersHandled) return;
    // If this is a Nexus redirect, handle that logic in the separate page component
    if (isNexusLanding()) return;

    // These values are set by pondoo-login-platform when redirecting and are
    // captured (and removed from the URL) at module load — see top of file.
    if (initialLoginToken && initialLoginRefreshToken) {
      appInitializationService.configureAppInitialization(
        initialLoginToken,
        initialLoginRefreshToken,
      );
      setParametersHandled(true);
    } else {
      // If we get here, we probably refreshed the app manually.
      // refreshAccessToken reads the stored refresh token and redirects to login
      // itself if it is missing or rejected by the server.
      refreshAccessToken().then((tokens) => {
        if (!tokens) return;
        appInitializationService.configureAppInitialization(
          tokens.accessToken,
          tokens.refreshToken,
        );
        setParametersHandled(true);
      });
    }
  }, [parametersHandled]);

  return <RouterProvider router={router} />;
};
export default App;
